import { isSupportedImage } from "./files";

interface LegacyFileEntry {
  isFile: true;
  isDirectory: false;
  file(success: (file: File) => void, failure?: (error: DOMException) => void): void;
  fullPath: string;
}

interface LegacyDirectoryReader {
  readEntries(
    success: (entries: LegacyEntry[]) => void,
    failure?: (error: DOMException) => void,
  ): void;
}

interface LegacyDirectoryEntry {
  isFile: false;
  isDirectory: true;
  createReader(): LegacyDirectoryReader;
  fullPath: string;
}

type LegacyEntry = LegacyFileEntry | LegacyDirectoryEntry;

interface LegacyDataTransferItem {
  webkitGetAsEntry?: () => LegacyEntry | null;
}

async function readFileEntry(entry: LegacyFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function readDirectory(entry: LegacyDirectoryEntry): Promise<LegacyEntry[]> {
  const reader = entry.createReader();
  const entries: LegacyEntry[] = [];
  while (true) {
    const batch = await new Promise<LegacyEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) break;
    entries.push(...batch);
  }
  return entries;
}

async function walk(entry: LegacyEntry): Promise<Array<{ file: File; path: string }>> {
  if (entry.isFile) {
    const file = await readFileEntry(entry);
    return isSupportedImage(file)
      ? [{ file, path: entry.fullPath.replace(/^\//, "") }]
      : [];
  }
  const children = await readDirectory(entry);
  const nested = await Promise.all(children.map(walk));
  return nested.flat();
}

export async function filesFromDrop(
  dataTransfer: DataTransfer,
): Promise<Array<{ file: File; path?: string }>> {
  const entries = Array.from(dataTransfer.items)
    .map(
      (item) =>
        (item as unknown as LegacyDataTransferItem).webkitGetAsEntry?.(),
    )
    .filter((entry): entry is LegacyEntry => Boolean(entry));
  if (entries.length > 0) {
    const nested = await Promise.all(entries.map(walk));
    return nested.flat();
  }
  return Array.from(dataTransfer.files)
    .filter(isSupportedImage)
    .map((file) => ({ file, path: file.webkitRelativePath || undefined }));
}
